/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */
import { EventBusModule } from "@vlinder-web/event-bus-module-react";
import { GraphqlServerModule } from "@vlinder-web/graphql-server-module-react";
import { BaseApplication } from "./base/baseApplication";
import { UnitTest } from "./unit.test";
import { StorageModule } from "@vlinder-web/storage-module-react";
import {
  AppNavigatorService,
  BreadcrumbService,
  LandingPageService,
  PrivacyPageService,
  TermsPageService,
  ProfilePageService,
  DashboardOverviewService,
  SignInPageService,
  SignUpPageService,
  ResetPasswordPageService,
  ForgotPasswordPageService,
  RegisterPageService,
  OrganizationPageService,
  MyProfilePageService,
  UsersListPageService,
  ProvisionalPageService,
  ProvisionalDetailPageService,
  ActualDetailPageService,
  ActualPageService,
  HoldersPageService,
  HoldersListPageService,
  CreditUnionPageService,
  UserListDetailPageService,
  CreditUnionDetailPageService,
  ProductLandingPageService,
  CreditUsersListPageService,
  ProductPageService,
  ProductDetailPageService,
  ProductAnalyticsService,
  ActivityPageService,
  AnalyticsAPIDetailPageService,
  WeatherAnalyticsPageService,
  PayoutPageService,
  PayoutListPageService,
 PayoutDetailPageService,
 AllEventPageService,
 ArchivePolicyPageService,
 TriggerPageService,
  FaqPageService,
} from "../services";

import { DEFAULT_APP_CONFIG as DEF_CONFIG } from "../config/default.config";
//import { mypoliciesdetailPageContext } from "../pages/ProvisionalDetailPage/machines/pageContent";

export class VApplication extends BaseApplication {
  private static instance: VApplication;

  private constructor(options: any = {}) {
    super(options);

    this.setupBindings();

    //Modules
    this.initModules();

    //Services
    this.initServices();
  }

  static getInstance(options: any = {}) {
    if (!this.instance) {
      this.instance = new VApplication(options);
    }
    return this.instance;
  }

  // @ts-ignore
  async start() {
    await this.initRunLevel();
    await this.unitTest();
  }

  setupBindings() {
    //------------->>>>>--------------//
    const storageService = StorageModule.getInstance();
    const DEFAULT_APP_CONFIG = storageService.get("appConfig", "session");

    this.configure("services.RegisterPageService", {
      loginRestAPI: DEFAULT_APP_CONFIG?.servers?.rest?.loginServer?.baseURL,
      headers: DEFAULT_APP_CONFIG?.headers,
      signerPK: DEF_CONFIG?.SIGNER_PK,
    });

    this.configure("services.SignInPageService", {
      loginRestAPI: DEFAULT_APP_CONFIG?.servers?.rest?.loginServer?.baseURL,
      headers: DEFAULT_APP_CONFIG?.headers,
    });
    //------------->>>>>--------------//
    this.configure("services.SignUpPageService", {
      loginRestAPI: DEFAULT_APP_CONFIG?.servers?.rest?.loginServer?.baseURL,
      headers: DEFAULT_APP_CONFIG?.headers,
      termsURL: DEFAULT_APP_CONFIG?.organization?.termsURL,
    });
    //------------->>>>>--------------//
    this.configure("services.ForgotPasswordPageService", {
      loginRestAPI: DEFAULT_APP_CONFIG?.servers?.rest?.loginServer?.baseURL,
      headers: DEFAULT_APP_CONFIG?.headers,
    });
    //------------->>>>>--------------//
    this.configure("services.ResetPasswordPageService", {
      loginRestAPI: DEFAULT_APP_CONFIG?.servers?.rest?.loginServer?.baseURL,
      headers: DEFAULT_APP_CONFIG?.headers,
      signerPK: DEF_CONFIG?.SIGNER_PK,
    });
    //------------->>>>>--------------//
  }

  async initRunLevel() {
    const storageSrv = StorageModule.getInstance();
    const DEFAULT_APP_CONFIG = storageSrv.get("appConfig", "session");
    //------------->>>>>--------------//
    const graphqlModule = this.getSync(
      "services.GraphqlServerModule"
    ) as GraphqlServerModule;
    if (graphqlModule) {
      type TClient = (typeof DEFAULT_APP_CONFIG.servers.graphql)[0];
      DEFAULT_APP_CONFIG?.servers.graphql.forEach((client: TClient) => {
        graphqlModule
          .createClient(client.name, {
            url: client.url,
            getContext: this.getGraphQLContext.bind(this),
          })
          .then((res) => {
            console.log(`initRunLevel check`, res);
          })
          .catch((err) => {
            console.log(`error in initRunLevel  ${err.message}`);
          });
      });
    }
    //------------->>>>>--------------//
    const storageService = this.getSync(
      "services.StorageModule"
    ) as StorageModule;
    await storageService.init();
  }

  async unitTest() {
    const ut = new UnitTest(VApplication);
    ut.run();
  }

  initModules() {
    this.service("services.EventBusModule", EventBusModule);
    this.service("services.GraphqlServerModule", GraphqlServerModule);
  }

  initServices() {
    this.service("services.AppNavigatorService", AppNavigatorService);
    this.service("services.BreadcrumbService", BreadcrumbService);
    this.service("services.LandingPageService", LandingPageService);
    this.service(
      "services.ProductLandingPageService",
      ProductLandingPageService
    );
    this.service("services.PrivacyPageService", PrivacyPageService);
    this.service("services.TermsPageService", TermsPageService);
    this.service("services.ProfilePageService", ProfilePageService);
    this.service("services.DashboardOverviewService", DashboardOverviewService);
    this.service("services.SignInPageService", SignInPageService);
    this.service("services.SignUpPageService", SignUpPageService);
    this.service(
      "services.ForgotPasswordPageService",
      ForgotPasswordPageService
    );
    this.service("services.ResetPasswordPageService", ResetPasswordPageService);
    this.service("services.RegisterPageService", RegisterPageService);
    this.service("services.OrganizationPageService", OrganizationPageService);
    this.service("services.MyProfilePageService", MyProfilePageService);
    this.service("services.UsersListPageService", UsersListPageService);
    this.service("services.ProvisionalPageService", ProvisionalPageService);
    this.service(
      "services.ProvisionalDetailPageService",
      ProvisionalDetailPageService
    );
    this.service("services.ActualDetailPageService", ActualDetailPageService);
    this.service("services.ActualPageService", ActualPageService);
    this.service("services.HoldersPageService", HoldersPageService);
    this.service("services.HoldersListPageService", HoldersListPageService);
    this.service("services.CreditUnionPageService", CreditUnionPageService);
    this.service(
      "services.UserListDetailPageService",
      UserListDetailPageService
    );
    this.service(
      "services.CreditUnionDetailPageService",
      CreditUnionDetailPageService
    );
    this.service(
      "services.CreditUsersListPageService",
      CreditUsersListPageService
    );
    this.service("services.ProductPageService", ProductPageService);
    this.service("services.ProductDetailPageService", ProductDetailPageService);
    this.service("services.ProductAnalyticsService", ProductAnalyticsService);
    this.service("services.ActivityPageService", ActivityPageService);
    this.service("services.WeatherAnalyticsPageService", WeatherAnalyticsPageService);
    this.service("services.AnalyticsAPIDetailPageService", AnalyticsAPIDetailPageService);
    this.service("services.PayoutPageService", PayoutPageService);
    this.service("services.PayoutListPageService", PayoutListPageService);
    this.service("services.PayoutDetailPageService", PayoutDetailPageService);
    this.service("services.AllEventPageService", AllEventPageService);
    this.service("services.ArchivePolicyPageService", ArchivePolicyPageService);
    this.service("services.ArchivePolicyPageService", ArchivePolicyPageService);
    this.service("services.TriggerPageService", TriggerPageService);
    this.service("services.FaqPageService", FaqPageService);
  }

  private getGraphQLContext() {
    const loginService = this.getSync(
      "services.AppNavigatorService"
    ) as AppNavigatorService;
    let token = loginService?.getToken();
    //FIXME:
    if (token) {
      return {
        headers: {
          authentication: token,
          headers: {
            // ...{ ...deviceInfoService?.getDeviceInfo() },
            // ...{ ...deviceInfoService?.getAppInfo() },
          },
        },
      };
    } else {
      return {
        headers: {
          headers: {
            // ...{ ...deviceInfoService?.getDeviceInfo() },
            // ...{ ...deviceInfoService?.getAppInfo() },
          },
        },
      };
    }
  }
}
